import { useQuery } from '@tanstack/react-query';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import api from '@/lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Building, Users, Package, Clock, Shield } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface AdminDashboardSummary {
  workspace: {
    name: string;
    plan_code: string;
    plan_name: string;
    trial_expires_at?: string;
  };
  users: {
    active: number;
  };
  modules: {
    enabled_count: number;
  };
}

export default function AdminDashboard() {
  const { currentWorkspace } = useWorkspace();
  const moduleAccess = useModuleAccess('workspace', currentWorkspace?.id);

  // Fetch admin dashboard summary
  const {
    data: summary,
    isLoading,
    error,
  } = useQuery<AdminDashboardSummary>({
    queryKey: ['admin', 'dashboard', 'summary', currentWorkspace?.id],
    queryFn: async () => {
      try {
        const res = await api.get(`/admin/dashboard/summary`, {
          params: {
            workspace_id: currentWorkspace?.id,
          },
        });
        return res.data;
      } catch (err: any) {
        // If endpoint doesn't exist (404), return fallback data
        if (err.response?.status === 404) {
          console.log('Admin dashboard endpoint not available, using fallback data');
          return {
            workspace: {
              name: currentWorkspace?.name || 'Workspace',
              plan_code: 'trial',
              plan_name: 'Trial',
              trial_expires_at: undefined,
            },
            users: {
              active: 0,
            },
            modules: {
              enabled_count: moduleAccess.enabledModulesCount || 0,
            },
          } as AdminDashboardSummary;
        }
        throw err;
      }
    },
    enabled: !!currentWorkspace?.id,
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Calculate trial days remaining
  const getTrialDaysRemaining = (expiresAt?: string): number | null => {
    if (!expiresAt) return null;
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const trialDaysRemaining = summary?.workspace.trial_expires_at
    ? getTrialDaysRemaining(summary.workspace.trial_expires_at)
    : null;

  const isTrial = summary?.workspace.plan_code === 'trial' || summary?.workspace.plan_code === 'free';
  
  // Fallback data if API is not available
  const displayData = summary || {
    workspace: {
      name: currentWorkspace?.name || 'Workspace',
      plan_code: 'trial',
      plan_name: 'Trial',
      trial_expires_at: undefined,
    },
    users: {
      active: 0,
    },
    modules: {
      enabled_count: moduleAccess.enabledModulesCount || 0,
    },
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load dashboard data. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <p className="text-muted-foreground">
          Overview for {displayData.workspace.name}
        </p>
      </div>

      {/* Stats Cards Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {/* Workspace Name */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Workspace Name</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold truncate" title={displayData.workspace.name}>
              {displayData.workspace.name}
            </div>
          </CardContent>
        </Card>

        {/* Plan */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Plan</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">
              {displayData.workspace.plan_code}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {displayData.workspace.plan_name}
            </p>
          </CardContent>
        </Card>

        {/* Trial Expiry */}
        {isTrial && trialDaysRemaining !== null ? (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Trial Expiry</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {trialDaysRemaining}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {trialDaysRemaining === 1 ? 'day remaining' : 'days remaining'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Plan Status</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <Badge variant="default">Paid</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Active subscription
              </p>
            </CardContent>
          </Card>
        )}

        {/* Active Users */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{displayData.users.active}</div>
            <p className="text-xs text-muted-foreground mt-1">
              active users
            </p>
          </CardContent>
        </Card>

        {/* Enabled Modules Count */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Enabled Modules</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{displayData.modules.enabled_count}</div>
            <p className="text-xs text-muted-foreground mt-1">
              modules enabled
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

